import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { RotateCcw, ArrowRight, Mail, Phone, PackageOpen, Loader2, LogIn } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { lookupGuestReturn } from "@/lib/guest-return.functions";

type Section = {
  emoji: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
};

const sections: Section[] = [
  {
    emoji: "🚫",
    title: "Ακύρωση παραγγελίας",
    paragraphs: [
      "Άλλαξες γνώμη; Δεν πειράζει! Η εκ μέρους του πελάτη ακύρωση της παραγγελίας του λόγω μεταμελείας γίνεται δεκτή μόνο εφόσον δεν έχει το είδος ακόμα τιμολογηθεί και δοθεί προς αποστολή. Η ακύρωση γίνεται μέσω αποστολής ηλεκτρονικού μηνύματος (e-mail) στην διεύθυνση info@mrpanda.gr, και ο πελάτης οφείλει να δηλώσει τα ακριβή στοιχεία της παραγγελίας του. Η ακύρωση δεν γίνεται αν δεν αποσταλεί σχετική επιβεβαίωση εκ μέρους του MrPanda με ηλεκτρονική αλληλογραφία προς τον πελάτη.",
      "Σε περίπτωση που ο πελάτης πριν από την εκ μέρους του δήλωση ακύρωσης έχει καταβάλει την αξία της εγκεκριμένης παραγγελίας του και το MrPanda την έχει ήδη εισπράξει, τότε οφείλει μέσα σε προθεσμία δεκατεσσάρων (14) ημερολογιακών ημερών να επιστρέψει στον πελάτη την εισπραχθείσα αξία αφού αφαιρεθούν τα τραπεζικά έξοδα και τα τυχόν έξοδα μεταφοράς. Η επιστροφή χρημάτων μπορεί να πραγματοποιηθεί με τον ίδιο τρόπο που εκτελέσατε την πληρωμή εκτός της αντικαταβολής.",
      "Σε περίπτωση που έχει δοθεί εντολή πληρωμής εκ μέρους του πελάτη αλλά τα χρήματα δεν έχουν εισπραχθεί από την εταιρεία μας (όπως πχ. στην περίπτωση εντολής χρέωσης πιστωτικής κάρτας όταν όμως η Τράπεζα δεν έχει ακόμα καταβάλει το τίμημα στην MrPanda), τότε η MrPanda υποχρεούται εντός της προθεσμίας των δεκατεσσάρων (14) εργασίμων ημερών να επισπεύσει κάθε απαραίτητη ενέργεια και δήλωση προς κάθε αρμόδιο φορέα (Τράπεζα κλπ) για τη γνωστοποίηση της ακύρωσης της παραγγελίας και την υποχρέωση απόδοσης κάθε χρεωθέντος σε βάρος του πελάτη ποσού. Στην περίπτωση αυτή η MrPanda θεωρείται ότι έχει εκπληρώσει σύννομα τις υποχρεώσεις της, και η αναίρεση κάθε χρέωσης θα γίνει με βάση τους όρους της σύμβασης που συνδέει τον πελάτη με τον τρίτο αυτό φορέα (Τράπεζα κλπ) και η MrPanda δεν φέρει καμία ευθύνη σχετικά με το χρόνο επιστροφής ή αντιλογισμού κάθε σχετικής χρέωσης.",
      "Αν η παραγγελία έχει εκτελεστεί (κι έχουν εκδοθεί ανάλογα παραστατικά) και ο πελάτης εξακολουθεί να επιθυμεί να ακυρώσει την παραγγελία, τότε υπόκειται στους όρους Επιστροφής Προϊόντων.",
      "Σε περίπτωση που μια παραγγελία επιστρέψει απαράδοτη στο κατάστημα (π.χ. λόγω λάθους στοιχείων διεύθυνσης, απουσίας παραλήπτη ή άρνησης παραλαβής), επιστρέφουμε στον πελάτη το ποσό που αντιστοιχεί στην αξία των προϊόντων, χωρίς τα μεταφορικά έξοδα. Εναλλακτικά, ο πελάτης μπορεί να ζητήσει την επαναποστολή της παραγγελίας με εκ νέου χρέωση των μεταφορικών εξόδων.",
      "*Το MrPanda διατηρεί το δικαίωμα να παραδίδει τα προϊόντα του σε διαφορετική συσκευασία από την φωτογραφιζόμενη εφόσον το προϊόν παραμένει ταυτόσημο με αυτό που έχει επιλεγεί από τον πελάτη χωρίς πρότερη ειδοποίηση (καθώς οι εταιρίες παραγωγής ενδέχεται να αλλάζουν τις μακέτες τους).",
    ],
  },
  {
    emoji: "📦",
    title: "Επιστροφή προϊόντων",
    paragraphs: [
      "Έχετε το δικαίωμα να ζητήσετε την επιστροφή των προϊόντων που αγοράσατε, εντός προθεσμίας 14 ημερολογιακών ημερών από την ημερομηνία που θα παραλάβετε τα προϊόντα (Ν. 2251/1994 και Οδηγία 2011/83/ΕΕ). Δεν γίνονται αντικαταστάσεις ή αλλαγές προϊόντων. Σε περίπτωση επιστροφής, επιστρέφουμε την αξία των προϊόντων.",
      "Για να γίνει δεκτή μια επιστροφή, το προϊόν θα πρέπει να πληροί τα παρακάτω:",
    ],
    bullets: [
      "Δεν έχει χρησιμοποιηθεί και βρίσκεται στην αρχική του συσκευασία.",
      "Συνοδεύεται από τα όποια καρτελάκια της κατασκευάστριας εταιρείας.",
      "Συνοδεύεται από την απόδειξη λιανικής πώλησης ή το τιμολόγιο.",
      "Το δέμα είναι συσκευασμένο σωστά, ώστε να μην δημιουργηθούν φθορές κατά τη μεταφορά στο προϊόν ή την συσκευασία.",
    ],
  },
  {
    emoji: "🔄",
    title: "Πώς κάνω επιστροφή;",
    paragraphs: [
      "Η διαδικασία επιστροφής είναι απλή και γρήγορη. Ακολούθησε τα παρακάτω βήματα:",
    ],
    bullets: [
      "Καταχώρησε το αίτημά σου online πατώντας το κουμπί «Καταχώρηση επιστροφής» στο τέλος της σελίδας. Εάν έχεις λογαριασμό, συνδέσου πρώτα. Εάν αγόρασες ως επισκέπτης, χρειάζεσαι τον αριθμό παραγγελίας και το email που χρησιμοποίησες.",
      "Διάλεξε τρόπο επιστροφής: δωρεάν παράδοση στο κατάστημα MrPanda ή επιστροφή με BoxNow (κόστος 3 € ανά δέμα, με δυνατότητα πολλαπλών δεμάτων).",
      "Συσκεύασε προσεκτικά τα προϊόντα στην αρχική τους συσκευασία μαζί με την απόδειξη ή το τιμολόγιο.",
      "Για BoxNow, θα λάβεις email με το voucher και τον κωδικό αποστολής. Για επιστροφή στο κατάστημα, έλα με το δέμα σου στο ωράριο λειτουργίας μας.",
      "Μόλις παραλάβουμε και ελέγξουμε τα προϊόντα, επιστρέφουμε τα χρήματά σου εντός 14 ημερών με τον ίδιο τρόπο πληρωμής που χρησιμοποίησες.",
    ],
  },
  {
    emoji: "🚚",
    title: "Ποιος πληρώνει τα μεταφορικά;",
    paragraphs: [
      "Το κόστος των μεταφορικών της επιστροφής της παραγγελίας, αλλά και της νέας αποστολής, επιβαρύνει τον πελάτη. Σε περίπτωση φθοράς κατά την μεταφορά ή παραλαβής λάθος προϊόντων, τα έξοδα επιστροφής επιβαρύνουν το MrPanda.",
      "Σε περίπτωση επιστροφής επιβάλλεται να αποστέλλεται το αίτημά σας γραπτώς με email στην διεύθυνση info@mrpanda.gr, στο οποίο θα αναφέρεται οπωσδήποτε ο κωδικός παραγγελίας, ο λόγος που το επιστρέφετε και ο κωδικός του προϊόντος με το οποίο θέλετε να το αντικαταστήσετε. Για οποιαδήποτε πληροφορία χρειάζεστε, μπορείτε να επικοινωνείτε μαζί μας τηλεφωνικά στο 2311 822 500.",
    ],
  },
  {
    emoji: "💶",
    title: "Επιστροφή χρημάτων",
    paragraphs: [
      "Εάν για οποιοδήποτε άλλο λόγο επιθυμείτε να επιστρέψετε το προϊόν που αγοράσατε, επιβάλλεται να αποστείλετε το αίτημά σας γραπτώς με email στην διεύθυνση info@mrpanda.gr με Αίτημα Επιστροφής Χρημάτων, εντός 14 ημερολογιακών ημερών από την ημερομηνία παραλαβής. Για οποιαδήποτε πληροφορία χρειάζεστε, μπορείτε να επικοινωνείτε μαζί μας τηλεφωνικά στο 2311 822 500.",
      "Στις περιπτώσεις επιστροφής χρημάτων επιβαρύνεστε με τα μεταφορικά έξοδα επιστροφής του προϊόντος, εκτός αν πρόκειται για ελαττωματικό ή λάθος προϊόν. Αφού πραγματοποιηθεί η επιστροφή και διαπιστωθεί πως το προϊόν δεν έχει χρησιμοποιηθεί, θα γίνει επιστροφή χρημάτων εντός 14 ημερών.",
      "Δηλώνουμε ρητά πως η επιστροφή χρημάτων πραγματοποιείται μόνο με τον ίδιο τρόπο που εξοφλήθηκε η παραγγελία. Εάν έχει γίνει με αντικαταβολή, ζητείται από τον πελάτη λογαριασμός τραπέζης για την καταβολή του ποσού.",
    ],
  },
];

function ReturnsPage() {
  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 py-10 sm:py-14">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 sm:p-12">
          <div className="pointer-events-none absolute -right-6 -top-8 text-8xl opacity-20 select-none">🐼</div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm font-medium text-primary">
            <RotateCcw className="h-4 w-4" />
            Χωρίς άγχος, με χαμόγελο
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Πολιτική Ακύρωσης &amp; Επιστροφών
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Άλλαξες γνώμη ή κάτι δεν πήγε όπως το ήθελες; Κανένα πρόβλημα! Εδώ θα βρεις με
            απλό τρόπο πώς ακυρώνεις μια παραγγελία, πώς επιστρέφεις ένα προϊόν και πώς
            παίρνεις πίσω τα χρήματά σου. 🐼
          </p>
        </div>

        {/* Sections */}
        <div className="mt-10 space-y-4">
          {sections.map((s, i) => (
            <Card
              key={i}
              className="group overflow-hidden border-border transition-shadow hover:shadow-lg"
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl transition-transform group-hover:scale-110">
                    {s.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-primary/70">{i + 1}.</span>
                      <h2 className="text-lg font-semibold tracking-tight">{s.title}</h2>
                    </div>
                    <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
                      {s.paragraphs.map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                      {s.bullets && (
                        <ul className="mt-2 space-y-2">
                          {s.bullets.map((b, k) => (
                            <li key={k} className="flex items-start gap-2">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Υπόδειγμα εντύπου υπαναχώρησης */}
        <div className="mt-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-2xl">
              📝
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold tracking-tight">Υπόδειγμα εντύπου υπαναχώρησης</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Έχεις δικαίωμα αναιτιολόγητης υπαναχώρησης εντός 14 ημερολογιακών ημερών από την
                παραλαβή (Ν. 2251/1994). Συμπλήρωσε και στείλε μας το παρακάτω έντυπο μόνο εφόσον
                επιθυμείς να υπαναχωρήσεις από τη σύμβαση — μπορείς επίσης να μας ενημερώσεις με
                οποιαδήποτε άλλη σαφή δήλωση.
              </p>
              <pre className="mt-4 overflow-x-auto whitespace-pre-wrap rounded-2xl bg-muted/50 p-4 text-xs leading-relaxed text-foreground sm:text-sm">{`Προς: ΑΣΛΑΝΟΓΛΟΥ ΑΙΚΑΤΕΡΙΝΗ ΒΑΣΙΛΕΙΟΣ (MrPanda)
Π. Λεβαντή 15Α, 564 29 Σταυρούπολη Θεσσαλονίκης
Email: info@mrpanda.gr — Τηλ: 2311 822 500

Γνωστοποιώ/Γνωστοποιούμε (*) με την παρούσα ότι υπαναχωρώ/
υπαναχωρούμε (*) από τη σύμβαση πώλησης των κάτωθι αγαθών (*):

— Αγαθά / περιγραφή: ...........................................
— Αριθμός παραγγελίας: .........................................
— Ημ/νία παραγγελίας (*)/παραλαβής (*): ........................
— Όνομα καταναλωτή(-ών): .......................................
— Διεύθυνση καταναλωτή(-ών): ...................................
— Ημερομηνία: .................................................
— Υπογραφή (μόνο εάν το έντυπο αποσταλεί σε χαρτί): ............

(*) Διαγράφεται η περιττή ένδειξη.`}</pre>
            </div>
          </div>
        </div>

        {/* Contact box */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-muted/40 p-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Mail className="h-5 w-5 text-primary" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground/70">Email</p>
              <a href="mailto:info@mrpanda.gr" className="font-medium text-foreground hover:text-primary">
                info@mrpanda.gr
              </a>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-muted/40 p-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Phone className="h-5 w-5 text-primary" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground/70">Τηλέφωνο</p>
              <a href="tel:+302311822500" className="font-medium text-foreground hover:text-primary">
                2311 822 500
              </a>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-6 rounded-3xl border border-border bg-muted/40 p-6 text-center sm:p-8">
          <p className="text-2xl">💬</p>
          <h2 className="mt-2 text-xl font-semibold">Χρειάζεσαι βοήθεια με μια επιστροφή;</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Στείλε μας μήνυμα με τα στοιχεία της παραγγελίας σου και θα σε εξυπηρετήσουμε άμεσα!
          </p>
          <Button asChild className="mt-4">
            <Link to="/contact">
              Επικοινώνησε μαζί μας
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/* Καταχώρηση επιστροφής */}
        <SubmitReturnCTA />
      </div>
    </StorefrontLayout>
  );
}

function SubmitReturnCTA() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const lookup = useServerFn(lookupGuestReturn);

  const [open, setOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasAccountEmail, setHasAccountEmail] = useState<string | null>(null);

  const onClick = () => {
    if (loading) return;
    if (user) {
      navigate({ to: "/account/returns" });
      return;
    }
    setHasAccountEmail(null);
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) return;
    setSubmitting(true);
    setHasAccountEmail(null);
    try {
      const res = await lookup({ data: { orderNumber: orderNumber.trim(), email: email.trim() } });
      if (!res.ok) {
        toast.error("Δεν βρέθηκε παραγγελία με αυτόν τον αριθμό και email.");
        return;
      }
      if (res.hasAccount) {
        setHasAccountEmail(email.trim());
        return;
      }
      if (res.guestToken) {
        setOpen(false);
        navigate({ to: "/order/$token", params: { token: res.guestToken }, search: { view: true } });
      } else {
        toast.error("Δεν είναι δυνατή η αυτόματη επιστροφή για αυτή την παραγγελία. Επικοινώνησε μαζί μας.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mt-6 rounded-3xl border border-primary/30 bg-primary/5 p-6 text-center sm:p-8">
        <p className="text-2xl">📮</p>
        <h2 className="mt-2 text-xl font-semibold">Καταχώρησε την επιστροφή σου online</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Γρήγορη διαδικασία με voucher BoxNow ή δωρεάν επιστροφή στο κατάστημα.
        </p>
        <Button onClick={onClick} disabled={loading} className="mt-4">
          <PackageOpen className="mr-2 h-4 w-4" />
          Καταχώρηση επιστροφής
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageOpen className="h-5 w-5 text-primary" />
              Επιστροφή χωρίς λογαριασμό
            </DialogTitle>
            <DialogDescription>
              Συμπλήρωσε τον αριθμό παραγγελίας και το email που χρησιμοποιήθηκε.
            </DialogDescription>
          </DialogHeader>

          {hasAccountEmail ? (
            <div className="space-y-4 py-2">
              <div className="rounded-xl border border-amber-400/30 bg-amber-50 p-4 text-sm dark:bg-amber-950/30">
                <p className="font-medium">Αυτό το email είναι συνδεδεμένο με λογαριασμό.</p>
                <p className="mt-1 text-muted-foreground">
                  Συνδέσου για να διαχειριστείς την επιστροφή σου με ασφάλεια.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setHasAccountEmail(null)}>
                  Πίσω
                </Button>
                <Button asChild className="flex-1">
                  <Link to="/login">
                    <LogIn className="mr-2 h-4 w-4" />
                    Σύνδεση
                  </Link>
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="ret-order">Αριθμός παραγγελίας</Label>
                <Input
                  id="ret-order"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="π.χ. 202606270005"
                  required
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ret-email">Email παραγγελίας</Label>
                <Input
                  id="ret-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  disabled={submitting}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                  Άκυρο
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Συνέχεια
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export const Route = createFileRoute("/returns")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/returns" },
      { title: "Πολιτική Ακύρωσης & Επιστροφών — MrPanda.gr" },
      {
        name: "description",
        content:
          "Πώς να ακυρώσεις παραγγελία, να επιστρέψεις προϊόν και να λάβεις επιστροφή χρημάτων από το MrPanda.gr — απλά και ξεκάθαρα.",
      },
      { property: "og:title", content: "Πολιτική Ακύρωσης & Επιστροφών — MrPanda.gr" },
      {
        property: "og:description",
        content: "Ακυρώσεις, επιστροφές προϊόντων και επιστροφές χρημάτων στο MrPanda.gr με φιλικό τρόπο.",
      },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/returns" }],
  }),
  component: ReturnsPage,
});